/**
 * Détermine le prochain numéro séquentiel à utiliser.
 *
 * @param existingNumbers Les numéros (séquentiels, entiers) déjà utilisés pour ce type/année.
 * @param sequenceNext Le prochain numéro que renverrait la séquence atomique (nextval).
 * @param reuseDeleted Si true, on comble les trous laissés par les suppressions (mode non conforme
 *                    à la réglementation tunisienne — voir réglage utilisateur).
 * @returns Un objet indiquant le numéro choisi et si la séquence doit être avancée.
 */
export function findNextNumber(
  existingNumbers: number[],
  sequenceNext: number,
  reuseDeleted: boolean,
): { value: number; consumeSequence: boolean } {
  if (!reuseDeleted) {
    return { value: sequenceNext, consumeSequence: true };
  }

  const used = new Set(existingNumbers);
  const max = existingNumbers.length === 0 ? 0 : Math.max(...existingNumbers);
  // Cherche le plus petit entier positif manquant dans [1..max]
  for (let i = 1; i <= max; i++) {
    if (!used.has(i)) {
      return { value: i, consumeSequence: false };
    }
  }
  // Aucun trou — on prend max+1, indépendamment de la séquence (qui peut avoir dérivé
  // derrière ou devant suite à des suppressions).
  return { value: max + 1, consumeSequence: false };
}

/**
 * Extrait la partie numérique d'un numéro de document au format {PREFIX}-{YEAR}-{NNNN}.
 * Retourne null si le format ne correspond pas.
 */
export function parseDocumentNumber(
  number: string,
): { prefix: string; year: number; value: number } | null {
  const match = /^([A-Z]+)-(\d{4})-(\d+)$/.exec(number);
  if (!match) return null;
  return {
    prefix: match[1]!,
    year: Number(match[2]),
    value: Number(match[3]),
  };
}
